import type { BirdEyeBoardState, BirdEyeDragSource, BirdEyeDropTarget } from '@elva/ui';
import type { BirdEyeVisitStatus } from '../api/types';

export type BirdEyeMoveRequest =
  | { kind: 'update'; appointmentId: number; status: BirdEyeVisitStatus }
  | { kind: 'unsupported'; reason: string }
  | { kind: 'noop' };

/**
 * Turns a drag & drop into the visit status the backend understands. The endpoint
 * only sets a status (the server owns the lane and the operatory), so moves it can't
 * express are reported as unsupported instead of being applied locally and lost on refresh.
 */
export function resolveBirdEyeMove(
  state: BirdEyeBoardState,
  from: BirdEyeDragSource,
  to: BirdEyeDropTarget,
): BirdEyeMoveRequest {
  const patientId =
    from.type === 'list' ? from.patientId : state.ops[from.index]?.seated?.id;
  const appointmentId = Number(patientId);
  if (!patientId || !Number.isFinite(appointmentId)) return { kind: 'noop' };

  if (from.type === 'list' && to.type === 'list' && from.lane === to.lane) return { kind: 'noop' };
  if (from.type === 'operatory' && to.type === 'operatory') {
    return from.index === to.index
      ? { kind: 'noop' }
      : { kind: 'unsupported', reason: 'Moving a patient to another operatory isn’t supported here.' };
  }

  if (to.type === 'operatory') {
    // SEATED can't unseat whoever is already in the room, so displacing them locally
    // would leave two seated patients for one operatory after the next refresh.
    const room = state.ops[to.index];
    if (room?.seated) {
      return { kind: 'unsupported', reason: `${room.room} is already occupied.` };
    }
    return { kind: 'update', appointmentId, status: 'SEATED' };
  }
  if (to.lane === 'waiting') return { kind: 'update', appointmentId, status: 'ARRIVED' };
  // The server only files a patient under Walking out once checked out; TREATMENT_DONE
  // keeps them in the operatory lane, so the card would snap back on the next refresh.
  if (to.lane === 'walking') return { kind: 'update', appointmentId, status: 'CHECKED_OUT' };
  return { kind: 'unsupported', reason: 'A patient can’t be moved back to Arriving.' };
}
